import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React, { Dispatch, SetStateAction } from "react";
import {
  OrganisationQuoteTemplatesFilters,
  ProjectType,
  TemplateSearchFiltersInput,
  TypeOfTemplate,
} from "../../../clients/graphqlTypes.js";
import {
  FilterChip,
  FilterChipGroup,
} from "../../commons/filters/FilterChip.js";
import { useCategories } from "../categories/useCategories.js";
import { OrganisationMembersFilterChips } from "../organisations/FilterOrganisationMembersChips.js";
import { useProjectTypes } from "../projects/useProjectTypes.js";
import { PropertiesFilterChips } from "../properties/PropertiesFilterChips.js";
import { TemplateFiltersFormValues } from "./filters/TemplateFilterFields.js";
import { useTypeOfTemplates } from "./quote/useTypeOfTemplates.js";
import { useTemplatesQuoteFilterChips_TemplateTypesQuery } from "./useTemplateDocumentsActiveFilterChips.generated.js";

interface Props<F> {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  fixedFilters?: Partial<F> & Partial<TemplateFiltersFormValues>;
}

export function useTemplateDocumentsAllAvailableActiveFilterChips<
  F extends Pick<
    TemplateSearchFiltersInput,
    | "createdAfter"
    | "createdBefore"
    | "createdByUserIds"
    | "priceMin"
    | "priceMax"
    | "tagsAny"
    | "tagsAll"
    | "templateTypeIds"
    | "categories"
    | "typeOfProjects"
    | "typeOfTemplates"
    | "properties"
  >,
>({ filters, setFilters, fixedFilters }: Props<F>): Array<JSX.Element> {
  const { projectTypeLabels } = useProjectTypes();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { resolveCategories } = useCategories();

  const client = useApolloClient();
  const templateTypesQuery = useTemplatesQuoteFilterChips_TemplateTypesQuery({
    client,
    variables: {
      ids: filters.templateTypeIds ?? [],
    },
    fetchPolicy: "cache-and-network",
    skip: !filters.templateTypeIds || filters.templateTypeIds.length === 0,
  });

  const templateTypes = React.useMemo(
    () =>
      getDataOrNull(
        (templateTypesQuery.data ?? templateTypesQuery.previousData)
          ?.templateTypes
      )?.templateTypes ?? [],
    [templateTypesQuery.data, templateTypesQuery.previousData]
  );

  const categories = React.useMemo(
    () =>
      !filters.categories || filters.categories.length === 0
        ? []
        : resolveCategories(filters.categories, {
            addParentsWhenNotIncluded: false,
            removeChildrenIfIncluded: false,
            excludeDeletedCategories: true,
          }),
    [resolveCategories, filters.categories]
  );

  const getTemplateTypeTitle = (id: string) =>
    templateTypes.find(templateType => templateType.id === id)?.title;

  const getCategoryTitle = (id: string) =>
    categories.find(c => c.id === id)?.name;

  const getTypeOfProjectTitle = (value: ProjectType) =>
    projectTypeLabels[value];

  const getTypeOfTemplateTitle = (value: TypeOfTemplate) =>
    typeOfTemplatesLabels[value];

  const getTemplateTypePropertyTitle = (key: string) => {
    for (const templateType of templateTypes) {
      const prop = templateType.props2.find(p => p.key === key);
      if (prop) return prop.label;
    }
  };

  const baseFilterChips = useTemplateDocumentsActiveFilterChips({
    filters,
    setFilters,
    fixedFilters,
  });

  const chips: Array<JSX.Element> = [];

  if (filters.templateTypeIds && filters.templateTypeIds.length > 0) {
    chips.push(
      <FilterChipGroup
        key="template-types"
        values={filters.templateTypeIds}
        setValues={
          isNil(fixedFilters?.templateTypeIds)
            ? newValues => {
                setFilters?.(filters => ({
                  ...filters,
                  templateTypeIds:
                    newValues && newValues.length > 0 ? newValues : null,
                }));
              }
            : undefined
        }
        getValueLabel={getTemplateTypeTitle}
      />
    );
  }

  if (filters.categories && filters.categories.length > 0) {
    chips.push(
      <FilterChipGroup
        key="categories"
        values={filters.categories}
        setValues={
          isNil(fixedFilters?.categories)
            ? newValues => {
                setFilters?.(filters => ({
                  ...filters,
                  categories:
                    newValues && newValues.length > 0 ? newValues : null,
                }));
              }
            : undefined
        }
        getValueLabel={getCategoryTitle}
      />
    );
  }

  if (filters.typeOfProjects && filters.typeOfProjects.length > 0) {
    chips.push(
      <FilterChipGroup<ProjectType>
        key="type-of-projects"
        values={filters.typeOfProjects}
        setValues={
          isNil(fixedFilters?.typeOfProjects)
            ? newValues => {
                setFilters?.(filters => ({
                  ...filters,
                  typeOfProjects:
                    newValues && newValues.length > 0 ? newValues : null,
                }));
              }
            : undefined
        }
        getValueLabel={getTypeOfProjectTitle}
      />
    );
  }

  if (filters.typeOfTemplates && filters.typeOfTemplates.length > 0) {
    chips.push(
      <FilterChipGroup<TypeOfTemplate>
        key="type-of-templates"
        values={filters.typeOfTemplates}
        setValues={
          isNil(fixedFilters?.typeOfTemplates)
            ? newValues => {
                setFilters?.(filters => ({
                  ...filters,
                  typeOfTemplates:
                    newValues && newValues.length > 0 ? newValues : null,
                }));
              }
            : undefined
        }
        getValueLabel={getTypeOfTemplateTitle}
      />
    );
  }

  chips.push(...baseFilterChips);

  if (filters.properties && filters.properties.length > 0) {
    chips.push(
      <PropertiesFilterChips
        key="properties"
        values={filters?.properties}
        setValues={
          isNil(fixedFilters?.properties)
            ? newValue =>
                setFilters?.(filters => ({
                  ...filters,
                  properties: newValue ?? null,
                }))
            : undefined
        }
        getKeyLabel={getTemplateTypePropertyTitle}
      />
    );
  }

  return chips;
}

export function useTemplateDocumentsActiveFilterChips<
  F extends Pick<
    OrganisationQuoteTemplatesFilters,
    | "createdAfter"
    | "createdBefore"
    | "createdByUserIds"
    | "priceMin"
    | "priceMax"
    | "tagsAny"
    | "tagsAll"
  >,
>({ filters, setFilters, fixedFilters }: Props<F>): Array<JSX.Element> {
  const { t } = useTranslate(["Templates", "Global"]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();

  const chips: Array<JSX.Element> = [];

  if (filters.createdByUserIds && filters.createdByUserIds.length > 0) {
    chips.push(
      <OrganisationMembersFilterChips
        key="created-by-user"
        values={filters?.createdByUserIds}
        setValues={
          isNil(fixedFilters?.createdByUserIds) && setFilters
            ? newValue =>
                setFilters(filterVariables => ({
                  ...filterVariables,
                  createdByUserIds: newValue,
                }))
            : undefined
        }
        label={t("Created by", { ns: "Global" })}
      />
    );
  }

  if (filters.createdAfter) {
    chips.push(
      <FilterChip
        key="created-after"
        label={t("Created after", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.createdAfter)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  createdAfter: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.createdAfter && getFormattedDate(filters.createdAfter)}
      />
    );
  }

  if (filters.createdBefore) {
    chips.push(
      <FilterChip
        key="created-before"
        label={t("Created before", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.createdBefore)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  createdBefore: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.createdBefore && getFormattedDate(filters.createdBefore)}
      />
    );
  }

  if (filters.priceMin) {
    chips.push(
      <FilterChip
        key="price-min"
        label={t("Min price", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.priceMin)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  priceMin: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.priceMin}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
    );
  }

  if (filters.priceMax) {
    chips.push(
      <FilterChip
        key="price-max"
        label={t("Max price", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.priceMax)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  priceMax: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.priceMax}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
    );
  }

  if (filters.tagsAny && filters.tagsAny.length > 0) {
    chips.push(
      <FilterChipGroup
        key="tags-any"
        label={t("Tag", { ns: "Templates" })}
        values={filters.tagsAny}
        getValueLabel={value => value}
        setValues={
          isNil(fixedFilters?.tagsAny)
            ? value =>
                setFilters(state => ({
                  ...state,
                  tagsAny: value,
                }))
            : undefined
        }
      />
    );
  }

  if (filters.tagsAll && filters.tagsAll.length > 0) {
    chips.push(
      <FilterChip
        key="tags-all"
        label={t("Tags (all)", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.tagsAll)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  tagsAll: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.tagsAll?.join?.(",")}
        getValueLabel={value => value}
      />
    );
  }

  return chips;
}
